import {
  ClaimBusinessBanner,
  MobileStickyCall,
  Navigation,
  getSessionId,
  hashTarget,
  installInteractionTracking,
  isPreviewMode,
  puckConfig,
  resolveNavigationStyleForPage,
  useJobsiteWebMcp,
  utmFromUrl,
  visibleSections,
} from "@servicepro/jobsite-components";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { SiteFooter, chromeForPage } from "./components/site-chrome";
import { NotFound } from "./pages/not-found";
import { PagesProvider } from "./contexts/pages-context";
import { Render } from "@puckeditor/core";
import { SEOHead } from "./components/seo-head";
import { mergeMetadata } from "./types/seo";
// oxlint-disable-next-line import/default -- Vite virtual module (vite-site-data.plugin.ts) the linter cannot resolve
import siteData from "virtual:site-data";
import { useEffect } from "react";

// Global config for tracking and forms (client-side only)
if (typeof window !== "undefined") {
  (window as any).__SERVICEPRO_CONFIG__ = {
    apiUrl: siteData.apiUrl,
    // SP-400; undefined on an older build, which disables the fallback rather than breaking.
    backupApiUrl: siteData.backupApiUrl,
    business: siteData.business,
    businessId: siteData.businessId,
    googleMapsApiKey: siteData.googleMapsApiKey,
    // A transparent header is fixed with no spacer, so the first in-flow
    // Section has to know to clear it or it renders underneath.
    navigationStyle: siteData.navigationConfig?.style,
    siteId: siteData.siteId,
    slug: siteData.slug,
    turnstileSiteKey: siteData.turnstileSiteKey,
    variant: siteData.variant,
  };
}

/**
 * Route tracker for analytics
 */
const RouteTracker = (): null => {
    const location = useLocation();

    useEffect(() => {
      // Skip analytics tracking in preview mode (e.g. owner viewing their own site)
      if (isPreviewMode()) {
        return;
      }

      const config = (window as any).__SERVICEPRO_CONFIG__,
        trackUrl = config?.apiUrl && `${config.apiUrl}/api/analytics/${config.siteId}/pageview`;

      if (!trackUrl) {
        return;
      }

      const sessionId = getSessionId("sp_session_id");

      fetch(trackUrl, {
        body: JSON.stringify({
          path: location.pathname,
          sessionId,
          variant: config?.variant,
          // Source attribution: the external referrer + any UTM, used server-side
          // Only when the session is first created (organic search has no UTM, so
          // The referrer is what distinguishes it from direct).
          referrer: document.referrer || undefined,
          utmParams: utmFromUrl(),
        }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      }).catch(() => {
        console.error("service_pro_error_code: 1991");
      });
    }, [location.pathname]);

    return null;
  },

  /** Components (Hero, EmergencyBand) read navigationStyle from the window
   *  config to pad for a fixed transparent nav; it has to reflect the CURRENT
   *  page's effective style. Mutating during render is deliberate: the readers
   *  render after this node in the same pass, and effects would be too late. */
  ApplyPageNavStyle = ({ style }: { style: string | undefined }): null => {
    if (typeof window !== "undefined") {
      const config = (window as { __SERVICEPRO_CONFIG__?: Record<string, unknown> })
        .__SERVICEPRO_CONFIG__;
      if (config) {
        config.navigationStyle = style;
      }
    }
    return null;
  };

/**
 * Where a page opens: the section its address names ("/#contact", the way a
 * one-page site links to its sections), else the top. Runs on the first load
 * too, after hydration, which used to throw the visitor back to the top of a
 * page the browser had already scrolled to the right section.
 * @returns {null} Nothing; it only scrolls.
 */
const ScrollOnNavigate = (): null => {
    const { pathname } = useLocation();

    useEffect(() => {
      const section = hashTarget(globalThis.location.hash, (id) => document.querySelector(`#${CSS.escape(id)}`));
      if (section) {
        section.scrollIntoView();
      } else {
        globalThis.scrollTo({ behavior: "auto", top: 0 });
      }
    }, [pathname]);

    return null;
  },

  AppRouter = () => {
    const navigate = useNavigate();

    useEffect(() => {
      const handleClick = (e: MouseEvent): void => {
        const target = e.target as HTMLElement,
          link = target?.closest("a");

        if (!link) {
          return;
        }

        const href = link.getAttribute("href");
        if (!href) {
          return;
        }

        const isInternal = href.startsWith("/") && !href.startsWith("//"),
          isAnchor = href.startsWith("#"),
          isExternal = /^(https?:|mailto:|tel:)/u.test(href);

        if (!isInternal || isExternal || isAnchor) {
          return;
        }

        // Allow opening in new tab with cmd/ctrl click
        if ((e as any).ctrlKey || (e as any).metaKey || (e as any).shiftKey) {
          return;
        }

        e.preventDefault();
        navigate(href);
      };

      document.addEventListener("click", handleClick);
      return (): void => {
        document.removeEventListener("click", handleClick);
      };
    }, [navigate]);

    // Install on-site interaction tracking (CTA taps: call/text/directions/email).
    // One delegated listener; tel:/mailto:/maps links are caught by inference, so
    // No per-CTA wiring is needed. Shares the pageview session id.
    useEffect(() => {
      const config = (window as any).__SERVICEPRO_CONFIG__;
      if (!config?.apiUrl || !config?.siteId) {
        return;
      }
      const sessionId = getSessionId("sp_session_id");
      return installInteractionTracking({
        endpoint: `${config.apiUrl}/api/analytics/${config.siteId}/event`,
        sessionId,
      });
    }, []);

    // Offer the site's own flows — services, availability, quote requests,
    // Booking — to a browsing agent as WebMCP tools, so it doesn't have to drive
    // The page to get a customer booked. No-op unless the browser supports it.
    useJobsiteWebMcp();

    const { pages, seoMetadata, navigationConfig, mobileStickyCallConfig } = siteData;

    if (!pages || pages.length === 0) {
      return <div style={{ padding: "20px" }}>No pages found in site data</div>;
    }

    return (
      <PagesProvider pages={pages.map(({ id, path, title }) => ({ id, path, title }))}>
        <ScrollOnNavigate />

        <Routes>
          {pages.map((page) => {

            const pageMetadata = mergeMetadata(seoMetadata, {
                ...page.seo,
                canonical: page.seo?.canonical || `${seoMetadata.domain}${page.path}`,
              }),

              builderData = page.builderData ? visibleSections(page.builderData) : null,
              chrome = chromeForPage(siteData, page.path),

              // A transparent nav only holds on pages that open with a Hero; any
              // Other first section (old Breadcrumb-first layouts, a hidden Hero)
              // Demotes it to solid so the header stays readable.
              navStyle = resolveNavigationStyleForPage(
                chrome.navigationConfig?.style,
                (builderData as { content?: { type?: string }[] } | null)?.content,
              ),
              pageNavigationConfig = chrome.navigationConfig
                ? { ...chrome.navigationConfig, style: navStyle }
                : chrome.navigationConfig,
              pageFooterConfig = chrome.footerConfig;

            return (
              <Route
                key={page.path}
                path={page.path}
                element={
                  <>
                    <SEOHead metadata={pageMetadata} faviconUrl={seoMetadata?.faviconUrl} />
                    <div className="jobsite-container jobsite:min-h-screen">
                      {/* Claim banner — only renders when ?claim= token is in URL */}
                      <ClaimBusinessBanner />

                      {/* Render Navigation if config exists */}
                      <ApplyPageNavStyle style={navStyle} />
                      {pageNavigationConfig && <Navigation {...pageNavigationConfig} />}

                      {/* Render page content */}
                      {builderData ? (
                        <Render
                          config={puckConfig}
                          data={{
                            ...builderData,
                            root: {
                              ...builderData.root,
                              props: {
                                ...builderData.root?.props,
                                theme: siteData.puckTheme,
                              },
                            },
                          }}
                          metadata={{
                            business: siteData.business,
                            onePage: siteData.onePage,
                            pagePath: page.path,
                            pagePaths: pages.map((p) => p.path),
                            site: {
                              googleMapsApiKey: (siteData as any).googleMapsApiKey,
                              id: siteData.siteId,
                              slug: siteData.slug,
                              title: siteData.seoMetadata?.defaultTitle,
                              url: seoMetadata.domain,
                            },
                          }}
                        />
                      ) : (
                        <div style={{ padding: "20px" }}>No content for page: {page.title}</div>
                      )}

                      {/* Render Footer if config exists */}
                      {pageFooterConfig && <SiteFooter {...pageFooterConfig} />}

                      {/* Render Mobile Sticky Call if config exists. The nav's
                          "Show Text Us" setting is the single source of truth, so
                          the sticky bar follows it (falling back to its own). */}
                      {mobileStickyCallConfig && (
                        <MobileStickyCall
                          {...mobileStickyCallConfig}
                          showTextUs={
                            navigationConfig?.showTextUs ??
                            mobileStickyCallConfig.showTextUs
                          }
                        />
                      )}
                    </div>
                  </>
                }
              />
            );
          })}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </PagesProvider>
    );
  },

  App = () => (
    <>
      <RouteTracker />
      <AppRouter />
    </>
  );

export { App };
